import React, { useState, useEffect } from "react";
import ShoppingListItem from "./ShoppingListItem";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { v4 as uuidv4 } from "uuid";
import QRCodeHandler from "../QRCodeHandler";

const ShoppingList = ({ addIngredient }) => {
    const [toBuy, setToBuy] = useState([]);
    const [bought, setBought] = useState([]);
    const [newItem, setNewItem] = useState({ name: "", amount: "", unit: "ks" });

    useEffect(() => {
        setToBuy(JSON.parse(localStorage.getItem("shoppingList")) || []);
        setBought(JSON.parse(localStorage.getItem("boughtList")) || []);
    }, []);

    const updateLocalStorage = (updatedToBuy, updatedBought) => {
        localStorage.setItem("shoppingList", JSON.stringify(updatedToBuy));
        localStorage.setItem("boughtList", JSON.stringify(updatedBought));
    };

    const addItem = () => {
        if (!newItem.name.trim() || !newItem.amount) return;

        const itemToAdd = { id: uuidv4(), ...newItem };
        const updatedToBuy = [...toBuy, itemToAdd];

        setToBuy(updatedToBuy);
        updateLocalStorage(updatedToBuy, bought);

        setNewItem({ name: "", amount: "", unit: "ks" });
    };

    const removeItemFromList = (id) => {
        const updatedToBuy = toBuy.filter(item => item.id !== id);
        setToBuy(updatedToBuy);
        updateLocalStorage(updatedToBuy, bought);
    };

    const onDragEnd = (result) => {
        try {
            if (!result.destination) return;

            const { source, destination } = result;
            let updatedToBuy = [...toBuy];
            let updatedBought = [...bought];

            if (source.droppableId === destination.droppableId) {
                const list = source.droppableId === "toBuy" ? updatedToBuy : updatedBought;
                const [movedItem] = list.splice(source.index, 1);
                list.splice(destination.index, 0, movedItem);

                if (source.droppableId === "toBuy") {
                    setToBuy(list);
                } else {
                    setBought(list);
                }
            } else {
                if (source.droppableId === "toBuy") {
                    const [movedItem] = updatedToBuy.splice(source.index, 1);
                    updatedBought.splice(destination.index, 0, movedItem);
                } else {
                    const [movedItem] = updatedBought.splice(source.index, 1);
                    updatedToBuy.splice(destination.index, 0, movedItem);
                }

                setToBuy(updatedToBuy);
                setBought(updatedBought);
            }

            updateLocalStorage(updatedToBuy, updatedBought);
        } catch (error) {
            console.error("❌ Chyba při přetahování:", error);
        }
    };

    const completePurchase = () => {
        if (bought.length === 0) return;

        bought.forEach((item) => {
            addIngredient({ ...item, expiration: "" });
        });

        setBought([]);
        updateLocalStorage(toBuy, []);
    };

    return (
        <Container>
            <h2>🛒 Nákupní seznam</h2>

            <Form className="mb-3">
                <Row>
                    <Col md={4} className="my-1">
                        <Form.Control
                            type="text"
                            placeholder="Název položky"
                            value={newItem.name}
                            onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                        />
                    </Col>
                    <Col md={3} className="my-1">
                        <Form.Control
                            type="number"
                            placeholder="Množství"
                            value={newItem.amount}
                            onChange={(e) => setNewItem({...newItem, amount: e.target.value})}
                            min="1"
                        />
                    </Col>
                    <Col md={3} className="my-1">
                        <Form.Select
                            value={newItem.unit}
                            onChange={(e) => setNewItem({...newItem, unit: e.target.value})}
                        >
                            <option value="ks">Kusy</option>
                            <option value="l">Litry</option>
                            <option value="g">Gramy</option>
                            <option value="ml">Mililitry</option>
                            <option value="kg">Kilogramy</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Button title="Přidat do nákupního seznamu" variant="success" onClick={addItem}>
                            <i className="bi bi-cart-plus"></i>
                        </Button>
                    </Col>
                </Row>
            </Form>

            <QRCodeHandler shoppingList={toBuy} addItemsFromQR={addItem}/>

            <DragDropContext onDragEnd={onDragEnd}>
                <Row>
                    <Col md={6} className="shopping-column to-buy">
                        <h4>📌 Chci koupit</h4>
                        <Droppable droppableId="toBuy">
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps} className="list-container">
                                    {toBuy.map((item, index) => (
                                        <Draggable key={String(item.id)} draggableId={String(item.id)} index={index}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                    <ShoppingListItem item={item} removeItem={removeItemFromList} isBought={false}/>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </Col>

                    <Col md={6} className="shopping-column bought">
                        <h4>✅ Koupeno</h4>
                        <Droppable droppableId="bought">
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps} className="list-container">
                                    {bought.map((item, index) => (
                                        <Draggable key={String(item.id)} draggableId={String(item.id)} index={index}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                    <ShoppingListItem item={item} isBought={true}/>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </Col>
                </Row>
            </DragDropContext>

            <Button variant="success" className="mt-3 me-2" onClick={completePurchase}>
                ✅ Ukončit nákup
            </Button>
        </Container>
    );
};

export default ShoppingList;
